import { useCallback, useContext, useEffect, useState } from "react";
import { getOrdinalSuffix } from "@/utils/numberUtils";
import { FiltersService, HttpService } from "@/services";
import styles from "./FilterCriteriaPanel.module.scss";
import { Loading, Overlay } from "@/shared-components";
import { INestedCriteria, ITreeItem } from "@/interfaces";
import { FiltersContext } from "@/contexts";
import { getTranslation } from "@/utils";
import { Fragment } from "react";

interface NestedCriteriaProps {
  item: INestedCriteria;
  index: number;
}

const NestedCriteria = ({ item, index }: NestedCriteriaProps) => {
  const { data, sub } = item;
  return (
    <>
      <div
        className={`${styles.panel} ${sub || index == 1 ? "" : styles.last}`}
        style={{ marginLeft: (index - 1) * 24 }}
      >
        <div className={styles["filter-name"]}>
          {data.filter_name} {index > 1 && `(${getOrdinalSuffix(index)})`}
        </div>
        <div>
          {data.criterias?.length
            ? data.criterias?.map((c, index) => (
                <Fragment key={index}>
                  {c.position > 0 && (
                    <div className={c.operation == "OR" ? styles.spaced : ""}>
                      {c.operation}
                    </div>
                  )}
                  <div>{c.text}</div>
                </Fragment>
              ))
            : data.is_auto_filter
              ? ""
              : getTranslation(
                  "filters-tree.modal.criteria.no-filter",
                  "No Filter Criteria",
                )}
        </div>
      </div>
      {sub && (
        <NestedCriteria key={data.filter_id} item={sub} index={index + 1} />
      )}
    </>
  );
};

interface FilterCriteriaPanelProps {
  filterId: string;
  data: ITreeItem;
  target: HTMLAnchorElement | null;
  show: boolean;
  onHide: () => void;
}

export const FilterCriteriaPanel = ({
  filterId,
  data,
  target,
  show,
  onHide,
}: FilterCriteriaPanelProps) => {
  const [criterias, setCriterias] = useState<INestedCriteria>();
  const [loading, setLoading] = useState(true);
  const { scope } = useContext(FiltersContext)!;
  const { projectId } = scope || {};

  const fetchData = useCallback(async () => {
    const url = `/projects/${projectId}/custom_views/filter_criteria_data/?id=${filterId}`;
    try {
      return await HttpService.get(url);
    } catch (error) {
      console.error(`error in fetch criteria data ${error}`);
    } finally {
      setLoading(false);
    }
  }, [filterId]);

  useEffect(() => {
    if (show) {
      fetchData().then((res) => {
        const criteriaTree = FiltersService.appendAutoFilterToCriteriaTree(
          res,
          data,
        );
        setCriterias(criteriaTree);
      });
    }
  }, [show]);

  return (
    <Overlay
      target={target}
      show={show}
      placement="right-start"
      rootClose
      onHide={onHide}
    >
      <div className={styles["filter-criteria-data"]}>
        {loading ? (
          <Loading size="modal" />
        ) : (
          criterias && (
            <>
              <div className={styles.title}>
                {getTranslation(
                  "filters-tree.modal.criteria.title",
                  "Filter Criteria",
                )}
              </div>
              <div className={styles.content}>
                <div className={styles["vertical-line"]}></div>
                <NestedCriteria item={criterias} index={1} />
              </div>
            </>
          )
        )}
      </div>
    </Overlay>
  );
};
